import {
  Modal,
  Text,
  Button,
  Stack,
  Select,
  Paper,
  Loader,
  Divider,
  Box,
  SimpleGrid,
  Badge,
  Collapse,
  Group,
  UnstyledButton,
  ThemeIcon,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useState, useEffect, useMemo, type KeyboardEvent } from "react";
import {
  IconBrain,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconLink,
  IconRocket,
  IconShieldLock,
} from "@tabler/icons-react";
import {
  getMyRoleOptions,
  getExpectedPartnerRoleOptions,
  getCollaborationRoleLabel,
} from "./constants/general.const";
import trpc from "@/shared/utils/trpc";

export interface ExpressInterestFormData {
  requesterRole: string;
  expectedPartnerRole: string;
  /** Set when the user confirms a suggested AI angle (stored on CollaborationMatch). */
  expressInterestTitle?: string | null;
  expressInterestSummary?: string | null;
}

export type ExpressInterestAiOption = {
  title: string;
  summary: string;
  requesterRole: string;
  expectedPartnerRole: string;
};

interface ExpressInterestModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (data: ExpressInterestFormData) => void;
  loading?: boolean;
  targetCompanyId: string;
  userWhoAmI: string[];
  targetCollaborationIntent: string[];
}

/** Turn summary into short lines for scannability (sentence splits, capped). */
function summaryToLines(summary: string, maxLines = 5): string[] {
  const trimmed = summary.trim();
  if (!trimmed) return [];
  const parts = trimmed.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) {
    return [trimmed];
  }
  return parts.slice(0, maxLines);
}

const ANGLE_ICONS = [IconLink, IconBrain, IconShieldLock, IconRocket] as const;

export default function ExpressInterestModal({
  opened,
  onClose,
  onConfirm,
  loading = false,
  targetCompanyId,
  userWhoAmI,
  targetCollaborationIntent,
}: ExpressInterestModalProps) {
  const [requesterRole, setRequesterRole] = useState<string | null>(null);
  const [expectedPartnerRole, setExpectedPartnerRole] = useState<string | null>(null);
  const [aiOptions, setAiOptions] = useState<ExpressInterestAiOption[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedAiIndex, setSelectedAiIndex] = useState<number | null>(null);
  const [manualOpened, { toggle: toggleManual }] = useDisclosure(false);

  const myRoleOptions = useMemo(() => {
    const base = getMyRoleOptions(userWhoAmI);
    if (requesterRole && !base.some((o) => o.value === requesterRole)) {
      return [...base, { value: requesterRole, label: getCollaborationRoleLabel(requesterRole) }];
    }
    return base;
  }, [userWhoAmI, requesterRole]);

  const partnerRoleOptions = useMemo(() => {
    const base = getExpectedPartnerRoleOptions(targetCollaborationIntent);
    if (expectedPartnerRole && !base.some((o) => o.value === expectedPartnerRole)) {
      return [
        ...base,
        { value: expectedPartnerRole, label: getCollaborationRoleLabel(expectedPartnerRole) },
      ];
    }
    return base;
  }, [targetCollaborationIntent, expectedPartnerRole]);

  const isValid = Boolean(requesterRole && expectedPartnerRole);
  const hasAiCards = !aiLoading && aiOptions.length >= 1;

  useEffect(() => {
    if (!opened) {
      setRequesterRole(null);
      setExpectedPartnerRole(null);
      setAiOptions([]);
      setAiLoading(false);
      setSelectedAiIndex(null);
      return;
    }

    if (!targetCompanyId.trim()) {
      setAiOptions([]);
      setAiLoading(false);
      setSelectedAiIndex(null);
      return;
    }

    let cancelled = false;
    setAiLoading(true);
    setAiOptions([]);
    setSelectedAiIndex(null);

    trpc.collaboration.suggestExpressInterestOptions
      .query({ targetCompanyId })
      .then((res) => {
        if (!cancelled) setAiOptions(res.options ?? []);
      })
      .catch(() => {
        if (!cancelled) setAiOptions([]);
      })
      .finally(() => {
        if (!cancelled) setAiLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [opened, targetCompanyId]);

  const handleSelectAiCard = (index: number, opt: ExpressInterestAiOption) => {
    setSelectedAiIndex(index);
    setRequesterRole(opt.requesterRole);
    setExpectedPartnerRole(opt.expectedPartnerRole);
  };

  const handleConfirm = () => {
    if (!isValid) return;
    const fromCard =
      selectedAiIndex !== null && aiOptions[selectedAiIndex] ? aiOptions[selectedAiIndex] : null;
    onConfirm({
      requesterRole: requesterRole!,
      expectedPartnerRole: expectedPartnerRole!,
      ...(fromCard
        ? {
            expressInterestTitle: fromCard.title,
            expressInterestSummary: fromCard.summary,
          }
        : {
            expressInterestTitle: null,
            expressInterestSummary: null,
          }),
    });
  };

  const cardKeyDown = (e: KeyboardEvent, index: number, opt: ExpressInterestAiOption) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelectAiCard(index, opt);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Express Interest"
      centered
      size={hasAiCards ? "1100px" : "md"}
      styles={{
        title: {
          fontSize: "20px",
          fontWeight: 600,
        },
        body: {
          paddingTop: hasAiCards ? 12 : undefined,
        },
        content: hasAiCards
          ? {
              maxWidth: "min(1100px, calc(100vw - 32px))",
            }
          : undefined,
      }}
    >
      {hasAiCards && (
        <Text size="sm" c="dimmed" mb="md" maw={900}>
          Pick <strong>one</strong> collaboration angle below. Each card summarizes how you could frame
          interest toward this partner and which roles apply. You can fine-tune roles under{" "}
          <em>Advanced</em> if needed.
        </Text>
      )}

      {!hasAiCards && !aiLoading && (
        <Text size="sm" c="dimmed" mb="lg">
          Choose the details about your collaboration intent. Suggested angles were unavailable; use the
          role fields below.
        </Text>
      )}

      {aiLoading && (
        <Box py="xl" style={{ display: "flex", justifyContent: "center" }}>
          <Loader size="sm" />
        </Box>
      )}

      {hasAiCards && (
        <Stack gap="md" mb="lg">
          <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
            <Text size="sm" fw={600}>
              Suggested collaboration angles
            </Text>
            <Badge variant="gradient" gradient={{ from: "primary.4", to: "primary.7", deg: 105 }} size="sm">
              AI-assisted
            </Badge>
          </Group>

          <Box
            style={{
              maxHeight: "min(62vh, 720px)",
              overflowY: "auto",
              overflowX: "hidden",
              marginInline: -4,
              paddingInline: 4,
            }}
          >
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              {aiOptions.map((opt, index) => {
                const selected = selectedAiIndex === index;
                const lines = summaryToLines(opt.summary);
                const yourRoleLabel = getCollaborationRoleLabel(opt.requesterRole);
                const theirRoleLabel = getCollaborationRoleLabel(opt.expectedPartnerRole);
                const AngleIcon = ANGLE_ICONS[index % ANGLE_ICONS.length];

                return (
                  <Paper
                    key={index}
                    component="article"
                    withBorder
                    p="lg"
                    radius="md"
                    onClick={() => handleSelectAiCard(index, opt)}
                    onKeyDown={(e) => cardKeyDown(e, index, opt)}
                    tabIndex={0}
                    role="button"
                    aria-pressed={selected}
                    aria-label={`Option ${index + 1}: ${opt.title}`}
                    styles={{
                      root: {
                        cursor: "pointer",
                        position: "relative",
                        outline: "none",
                        transition:
                          "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease, transform 160ms ease",
                        borderColor: selected
                          ? "var(--mantine-color-primary-6)"
                          : "var(--mantine-color-slate-3)",
                        borderWidth: selected ? 2 : 1,
                        backgroundColor: selected
                          ? "var(--mantine-color-primary-0)"
                          : "var(--mantine-color-body)",
                        boxShadow: selected
                          ? "0 4px 20px rgba(4, 48, 92, 0.12)"
                          : "0 1px 3px rgba(0, 0, 0, 0.04)",
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        "&:hover": {
                          borderColor: selected
                            ? "var(--mantine-color-primary-7)"
                            : "var(--mantine-color-slate-5)",
                          boxShadow: selected
                            ? "0 6px 24px rgba(4, 48, 92, 0.14)"
                            : "0 4px 14px rgba(0, 0, 0, 0.06)",
                        },
                        "&:focus-visible": {
                          outline: "2px solid var(--mantine-color-primary-5)",
                          outlineOffset: 2,
                        },
                      },
                    }}
                  >
                    {selected && (
                      <Box
                        style={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: "var(--mantine-color-primary-6)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <IconCheck size={16} color="white" stroke={2.5} />
                      </Box>
                    )}

                    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm" mb="sm">
                      <ThemeIcon
                        size={44}
                        radius="md"
                        variant="light"
                        color={selected ? "primary" : "slate"}
                        style={{ flexShrink: 0 }}
                      >
                        <AngleIcon size={22} stroke={1.6} />
                      </ThemeIcon>
                      <Badge
                        size="sm"
                        variant="outline"
                        color={selected ? "primary" : "slate"}
                        ml="auto"
                        style={{ flexShrink: 0 }}
                      >
                        Option {index + 1}
                      </Badge>
                    </Group>

                    <Text size="md" fw={700} lh={1.35} mb="md" pr={selected ? 32 : 0} c="slate.8">
                      {opt.title}
                    </Text>

                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={8} lts={0.6}>
                      Suggested wording
                    </Text>
                    <Stack component="ul" gap={8} mb="md" style={{ flex: 1, margin: 0, paddingLeft: 18 }}>
                      {lines.map((line, i) => (
                        <Text
                          component="li"
                          key={i}
                          size="sm"
                          c="slate.7"
                          style={{ lineHeight: 1.55 }}
                        >
                          {line}
                        </Text>
                      ))}
                    </Stack>

                    <Divider my="sm" label="Roles on the platform" labelPosition="left" />

                    <Box
                      p="sm"
                      mb="md"
                      style={{
                        backgroundColor: "var(--mantine-color-slate-0)",
                        borderRadius: 8,
                        border: "1px solid var(--mantine-color-slate-2)",
                      }}
                    >
                      <Text size="xs" c="dimmed" fw={600} mb={4}>
                        Your position
                      </Text>
                      <Text size="sm" mb="sm" lh={1.5} c="slate.8">
                        {yourRoleLabel}
                      </Text>
                      <Text size="xs" c="dimmed" fw={600} mb={4}>
                        Their expected role
                      </Text>
                      <Text size="sm" lh={1.5} c="slate.8">
                        {theirRoleLabel}
                      </Text>
                    </Box>

                    <Button
                      type="button"
                      variant={selected ? "filled" : "light"}
                      color="primary"
                      fullWidth
                      mt="auto"
                      size="sm"
                      rightSection={selected ? <IconCheck size={16} /> : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAiCard(index, opt);
                      }}
                    >
                      {selected ? "Selected" : "Select this angle"}
                    </Button>
                  </Paper>
                );
              })}
            </SimpleGrid>
          </Box>

          <Text size="xs" c="dimmed" ta="center" px="xs">
            Suggestions are generated from both company profiles and may be inaccurate — review before
            sending.
          </Text>
        </Stack>
      )}

      {hasAiCards && (
        <>
          <UnstyledButton
            onClick={toggleManual}
            type="button"
            mb="sm"
            style={{ width: "100%" }}
          >
            <Group gap={6} justify="center">
              {manualOpened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
              <Text size="sm" c="blue" fw={500}>
                {manualOpened ? "Hide" : "Advanced"}: set roles manually
              </Text>
            </Group>
          </UnstyledButton>
          <Collapse in={manualOpened}>
            <Stack gap="md" mb="md">
              <Select
                label="What role am I playing in this collaboration?"
                placeholder="select option"
                data={myRoleOptions}
                value={requesterRole}
                onChange={(v) => {
                  setRequesterRole(v);
                  setSelectedAiIndex(null);
                }}
                withAsterisk
                clearable
                searchable
              />
              <Select
                label="Expected Partner Role"
                placeholder="select option"
                data={partnerRoleOptions}
                value={expectedPartnerRole}
                onChange={(v) => {
                  setExpectedPartnerRole(v);
                  setSelectedAiIndex(null);
                }}
                withAsterisk
                clearable
                searchable
              />
            </Stack>
          </Collapse>
        </>
      )}

      {!hasAiCards && !aiLoading && (
        <>
          <Divider label="Collaboration roles" labelPosition="center" my="md" />
          <Stack gap="md">
            <Select
              label="What role am I playing in this collaboration?"
              placeholder="select option"
              data={myRoleOptions}
              value={requesterRole}
              onChange={(v) => {
                setRequesterRole(v);
                setSelectedAiIndex(null);
              }}
              withAsterisk
              clearable
              searchable
            />
            <Select
              label="Expected Partner Role"
              placeholder="select option"
              data={partnerRoleOptions}
              value={expectedPartnerRole}
              onChange={(v) => {
                setExpectedPartnerRole(v);
                setSelectedAiIndex(null);
              }}
              withAsterisk
              clearable
              searchable
            />
          </Stack>
        </>
      )}

      <Button fullWidth onClick={handleConfirm} loading={loading} disabled={!isValid} mt="lg" size="md">
        Confirm & Proceed
      </Button>
    </Modal>
  );
}
